import React, { useState } from 'react';
import moment from 'moment';
import {
  isRollingOfferCode,
  isWebOfferPrizeCode,
  WCH_HOTEL
} from 'utils/rewards';
import { Table, Label, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import WindcreekModal from 'components/WindcreekModal';
import Button from 'components/Button';
import { hotelOfferShape, patronRewardShape } from 'utils/shapes';

const MobileOfferLabel = styled(Label)`
  font-weight: bold;
  margin-right: 0.5em;
`;

const ModalButtons = styled.div`
  text-align: center;
  & > :not(:last-child) {
    margin-right: 0.25em;
  }

  & > :not(:first-child) {
    margin-left: 0.25em;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;

  ${MobileOfferLabel} {
    display: none;
  }

  @media screen and (max-width: ${props => props.theme.breakpoints.small}) {
    &&& {
      tbody {
        max-height: 20rem;
      }

      thead {
        display: none;
      }

      tr {
        display: block;
        background: rgba(0, 0, 0, 0.2);
        margin: 0.2em 0;
        padding: 0.3em;
        border-radius: 0.3em;

        &.selected {
          background: #fff;
          color: ${props => props.theme.buttons.primary.backgroundColor};
        }

        &.inactive > td {
          color: ${props => props.theme.colors.darkGrey};
        }
      }

      td:first-child > small {
        font-size: 1em;
      }

      td,
      td:first-child,
      td:last-child {
        display: block;
        width: 100%;
        font-size: 0.8em;
        padding: 0;
      }

      ${MobileOfferLabel} {
        display: inline;
      }
    }
  }
`;

const OfferDisclaimer = styled.p`
  flex: 0 1 auto;
  margin: 0;
  padding: 0 0.5rem;
  line-height: 1.15rem;
`;

function getOfferName(offer) {
  if (isWebOfferPrizeCode(offer.patronReward.prizeCode)) {
    return offer.patronReward.prizeName;
  }

  const maxPrizesText =
    offer.patronReward.prizeCode === WCH_HOTEL
      ? `${offer.patronReward.maxPrizesPerPeriod} per 30 days`
      : isRollingOfferCode(offer.patronReward.prizeCode)
      ? `${offer.patronReward.maxPrizesPerPeriod} per 14 days`
      : `${offer.patronReward.maxPrizesPerPeriod}`;

  return `${maxPrizesText} Comp or Discount (${offer.numberOfNights} remaining)`;
}

function CurrentOffers({
  loading,
  error,
  offersList,
  isHostBooking,
  startDate,
  endDate,
  authenticatedNonAdmin,
  isAdmin,
  sortedOffers,
  autoSelectedOffers,
  handleSelectedOffer
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const numOffersSelected = sortedOffers.filter(offer => offer.isSelected)
    .length;

  const handleOfferClick = offer => {
    if (isAdmin && startDate && endDate) {
      if (offer.isSelected && numOffersSelected === 1) {
        setSelectedOffer(offer);
        setIsOpen(true);
      } else {
        handleSelectedOffer(offer, autoSelectedOffers);
      }
    }
  };

  const handleConfirmDeselect = () => {
    handleSelectedOffer(selectedOffer, autoSelectedOffers);
    setSelectedOffer(null);
    setIsOpen(false);
  };

  return (
    <>
      {!loading && error ? (
        <p>{error}</p>
      ) : (
        !loading &&
        offersList &&
        offersList.length === 0 && (
          <p>You have no available hotel offers to redeem.</p>
        )
      )}
      {!loading && offersList && offersList.length > 0 && (
        <>
          {isHostBooking && (!startDate && !endDate) && (
            <p>Please select a date range before selecting an offer.</p>
          )}
          <div
            className={`offers-list ${
              offersList.length > 2 ? 'scrollable' : ''
            } ${authenticatedNonAdmin ? 'extraHeight' : ''}`}
          >
            <StyledTable size="sm" borderless>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Dates Valid</th>
                </tr>
              </thead>
              <tbody>
                {sortedOffers.map((offer, i) => (
                  <tr
                    key={`${offer.patronReward.prizeCode}-${i}`}
                    className={`${offer.isSelected ? 'selected' : ''} ${
                      isAdmin ? 'rowOffer' : ''
                    }${offer.inactive}`}
                    onClick={() => handleOfferClick(offer)}
                  >
                    <td>
                      <small>
                        <MobileOfferLabel>Prize Code:</MobileOfferLabel>
                        {offer.patronReward.prizeCode}
                      </small>
                    </td>
                    <td>
                      <MobileOfferLabel>Prize Name:</MobileOfferLabel>
                      {getOfferName(offer)}
                    </td>
                    <td>
                      <MobileOfferLabel>Dates Valid:</MobileOfferLabel>
                      {isRollingOfferCode(offer.patronReward.prizeCode) ? (
                        '---'
                      ) : (
                        <>
                          {moment(offer.bookingStartDate).format('MM/DD')}-
                          {moment(offer.bookingEndDate).format('MM/DD')}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </div>
          <OfferDisclaimer>
            <small>
              Offer eligibility is based on your casino play and current hotel
              rates
            </small>
          </OfferDisclaimer>
        </>
      )}
      <WindcreekModal isOpen={isOpen} centered autoFocus>
        <ModalBody>
          <h1>Are you sure?</h1>
          <p>Are you sure you want to book at cash rates without a comp?</p>
          <ModalButtons>
            <Button onClick={handleConfirmDeselect}>Confirm</Button>
            <Button secondary onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
          </ModalButtons>
        </ModalBody>
      </WindcreekModal>
    </>
  );
}

CurrentOffers.propTypes = {
  loading: PropTypes.bool,
  selectedOffers: PropTypes.objectOf(hotelOfferShape),
  error: PropTypes.any,
  authenticatedNonAdmin: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isHostBooking: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  handleSelectedOffer: PropTypes.func,
  autoSelectedOffers: PropTypes.arrayOf(
    PropTypes.shape({
      bookingStartDate: PropTypes.string,
      bookingEndDate: PropTypes.string,
      isSelected: PropTypes.bool,
      name: PropTypes.string,
      numberOfNights: PropTypes.number,
      patronReward: patronRewardShape
    })
  ),
  sortedOffers: PropTypes.arrayOf(
    PropTypes.shape({
      bookingStartDate: PropTypes.string,
      bookingEndDate: PropTypes.string,
      inactive: PropTypes.string,
      isSelectable: PropTypes.bool,
      isSelected: PropTypes.bool,
      name: PropTypes.string,
      numberOfNights: PropTypes.number,
      patronReward: patronRewardShape
    })
  )
};

export default CurrentOffers;
